import { z } from 'zod';
import { PAYMENT_METHODS, TEMPLATE_KINDS } from './contracts';
import { VAT_RATES } from './offers';
import { discountSchema } from './schemas-offers';
import './schemas';

const keys = <T extends Record<string, unknown>>(o: T) => Object.keys(o) as [keyof T & string, ...(keyof T & string)[]];
const str = (max: number) => z.string().trim().max(max);
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data w formacie RRRR-MM-DD');
const gr = z.number().int().min(0).max(100_000_000_00);

export const templateCreateSchema = z.object({
  kind: z.enum(keys(TEMPLATE_KINDS)),
  companyId: z.string().uuid().nullable().default(null),
  name: str(200).min(3, 'Podaj nazwę szablonu'),
  bodyHtml: str(400_000).default(''),
});

export const templateUpdateSchema = z.object({
  name: str(200).min(3),
  companyId: z.string().uuid().nullable(),
  active: z.boolean(),
  isDefault: z.boolean(),
});

export const templateVersionSaveSchema = z.object({
  bodyHtml: str(400_000).min(10, 'Szablon jest pusty'),
  note: str(500).default(''),
});

export const contractCreateSchema = z.object({
  caseId: z.string().uuid(),
  scopeIds: z.array(z.string().uuid()).min(1, 'Wybierz co najmniej jeden zakres prac'),
});

export const contractItemSchema = z.object({
  scopeId: z.string().uuid(),
  components: z.array(z.object({ itemId: z.string().uuid().nullable(), name: str(300), quantity: z.number().positive(), unit: str(10) })).max(50).default([]),
  name: str(300).min(1),
  description: str(2000).default(''),
  unit: str(10).min(1),
  quantity: z.number().positive().max(1_000_000),
  unitPriceGr: gr,
  discount: discountSchema.default(null),
  vatRate: z.number().refine((v) => (VAT_RATES as readonly number[]).includes(v), 'Niedozwolona stawka VAT'),
  /** pozycja katalogu (z oferty) i oznaczenie „urządzenie” (lista numerów seryjnych w protokole odbioru) */
  catalogItemId: z.string().uuid().nullable().default(null),
  device: z.boolean().default(false),
});

export const scheduleItemSchema = z.object({
  id: z.string().uuid().nullable().default(null),
  name: str(200).min(2, 'Podaj nazwę płatności'),
  amountGr: gr.min(1, 'Kwota płatności musi być większa od zera'),
  dueDate: date.nullable().default(null),
  conditionsStart: z.boolean().default(false),
  /** D-14: płatnik inny niż klient, np. program dotacyjny */
  payerLabel: str(200).default(''),
  // rozliczenie (docs/10 §3.5) — brak w żądaniu: wartości domyślne z ustawień firmy
  kind: z.enum(['zaliczka', 'rata', 'koncowa', 'inna']).optional(),
  trigger: z.enum(['podpis_umowy', 'zaplanowanie', 'rozpoczecie_prac', 'zakonczenie_prac', 'odbior', 'termin', 'reczny']).optional(),
  documentRequired: z.enum(['brak', 'proforma', 'zaliczkowa', 'koncowa']).optional(),
  requiredForClose: z.boolean().optional(),
  /** QA P13: dni na zapłatę od zdarzenia (pozycja bez daty); null — ustawienie firmy */
  eventDays: z.number().int().min(0, 'Liczba dni nie może być ujemna').max(120, 'Najwyżej 120 dni').nullable().default(null),
});

export const contractSaveSchema = z.object({
  title: str(300).min(3),
  payerId: z.string().uuid(),
  executionTerm: str(500).default(''),
  depositRequired: z.boolean(),
  templateVersionId: z.string().uuid().nullable().default(null),
  bodyHtml: str(400_000).default(''),
  items: z.array(contractItemSchema).max(300),
  schedule: z.array(scheduleItemSchema).max(50),
  /** aneks: pozycje harmonogramu umowy anulowane tym aneksem (tylko nieopłacone) */
  cancelScheduleItemIds: z.array(z.string().uuid()).default([]),
  /** aneks: pozycje to dodatkowe prace (planowanie i odbiór po podpisie); false — tylko zmiana cen / warunków */
  annexWorks: z.boolean().default(true),
});
export type ContractSaveInput = z.infer<typeof contractSaveSchema>;

export const contractSignSchema = z.object({
  signedAt: date,
  signedBy: str(300).min(3, 'Podaj, kto podpisał umowę'),
  note: str(1000).default(''),
});

// powód wymagany przy anulowaniu wydanej / podpisanej umowy (sprawdza serwer); roboczą można usunąć bez powodu
export const contractCancelSchema = z.object({ reason: str(500).default('') });

export const paymentSchema = z.object({
  paymentItemId: z.string().uuid().nullable().default(null),
  amountGr: gr.min(1, 'Kwota wpłaty musi być większa od zera'),
  paidAt: date,
  method: z.enum(keys(PAYMENT_METHODS)),
  reference: str(200).default(''),
  note: str(500).default(''),
});
export type PaymentInput = z.infer<typeof paymentSchema>;

/** QA qa10311123 3.4: „Klient zgłosił wpłatę” — opiekun odnotowuje informację od klienta (bez księgowania), księgowość dostaje powiadomienie. */
export const paymentClaimSchema = z.object({
  paidAt: date,
  amountGr: gr.min(1, 'Kwota musi być większa od zera').nullable().default(null),
  note: str(500).default(''),
});
export type PaymentClaimInput = z.infer<typeof paymentClaimSchema>;

export const waiverSchema = z.object({ reason: str(500).min(3, 'Podaj uzasadnienie odstępstwa'), until: date.nullable().default(null) });
